import {useNavigate, useParams} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';
import {useRef, useLayoutEffect, useState, useMemo, useCallback} from 'react';
import {useWindowDimensions, StyleSheet, RefreshControl, View, GestureResponderEvent} from 'react-native';
import {RecyclerListView, LayoutProvider, DataProvider} from 'recyclerlistview';
import {usePopup} from 'extensions/viewport/usePopup';
import {FileListHeader} from 'interface/stacks/FileListHeader';
import {FileListItem, FileListItemLayout} from 'interface/stacks/FileListItem';
import {load, preview, download} from 'utils/mediafire';
import {isTouch, isWeb} from 'utils/platform';
import {breakpoints, colors} from 'config/styles';
import {selectors} from 'store/slices/files';
import {slices} from 'store';
import events from 'extensions/events';

import * as app from 'store/slices/app';

import type {LayoutChangeEvent} from 'react-native';
import type {FilesItem} from 'store/slices/files/types';

const gutterSize = 16;

export interface FileListProps {
  view: {folders: string[], files: string[]},
  items: {[id: string]: FilesItem},
  layout?: FileListItemLayout,
  onOpen?: (item: FilesItem) => void,
  options?: {
    hideFolders?: boolean,
    hideFiles?: boolean,
    hideIcons?: boolean,
    hideExtraIcons?: boolean,
    hideHeader?: boolean,
    hideGutter?: boolean,
    linkParent?: boolean,
    disableOpen?: boolean,
    disableDrag?: boolean,
    disableFocus?: boolean,
    disableSelect?: boolean,
    disablePullToRefresh?: boolean,
  },
}

export function FileList(props: FileListProps) {
  const {folderKey} = useParams<"folderKey">();
  const focusedRef = useRef('');
  const screen = useWindowDimensions();
  const filelist = useRef<RecyclerListView<any, any>>();
  const widthUsed = useRef((screen.width < breakpoints.menu ? 0 : 200));
  const widthFree = useRef(screen.width - widthUsed.current);
  const [width, setWidth] = useState(widthFree.current);
  const [focusedI, setFocusedI] = useState(-1);
  const [isLoading, setLoading] = useState(false);
  const [rngSelect, setRngSelect] = useState(false);
  const isPremium = useSelector(app.selectors.isPremium);
  const selection = useSelector(selectors.getSelection);
  const dragging = useSelector(selectors.getDrag);
  const layout = useSelector(selectors.getLayout);
  const hasPrivate = useSelector(selectors.hasPrivateItems(selection));

  const view = props.view;
  const items = props.items;
  const options = props.options;
  const viewtype = props.layout || layout;
  const hasHeader = !options?.hideHeader;
  const hasGutter = !options?.hideGutter;
  const hasOpen = !options?.disableOpen;
  const hasSelect = !options?.disableSelect;
  const hasFocus = !options?.disableFocus;
  const hasDrag = !options?.disableDrag;
  const hasFiles = !options?.hideFiles && view?.files.length > 0;
  const hasFolders = !options?.hideFolders && view?.folders.length > 0;
  const hasPullToRefresh = !options?.disablePullToRefresh;
  const hasGridView = viewtype >= FileListItemLayout.GridSimple;
  // Header columns disabled
  const hasColumns = false; //!hasGridView && width >= breakpoints.columns;

  // List data
  const itemList = useMemo(() => (hasFiles || hasFolders) ?
    (hasFolders && hasFiles
      ? [
          hasHeader && 'folders', ...view.folders,
          hasHeader && 'files', ...view.files,
        ]
      : hasFiles
        ? [hasHeader && 'files', ...view.files]
        : [hasHeader && 'folders', ...view.folders]).filter(e => !!e)
    : [], [view, options, hasFiles, hasFolders]);

  const focused = focusedRef.current;
  const extended = useMemo(() => ({items, focused, selection, dragging}), [items, focused, selection, dragging]);
  const provider = useRef(new DataProvider((a, b) => a !== b, (i) => itemList[i] || i.toString()));
  const listData = useMemo(() => provider.current.cloneWithRows(itemList), [itemList]);
  const listGutter = useMemo(() => hasGutter && width > breakpoints.menu ? gutterSize : 0, [width]);
  const listLayout = useMemo(() => new LayoutProvider(() => viewtype, (type, rect, index) => {
    const widthFull = width - (listGutter * 2);
    const cellSize = 130;
    const folders = view.folders.length;
    if (hasHeader && (index === 0 || folders > 0 && index === folders + 1)) {
      rect.width = widthFull;
      rect.height = 32;
      return;
    }
    switch (type) {
      case FileListItemLayout.ListNormal:
        rect.width = widthFull;
        rect.height = isTouch() ? 60 : 41;
        return;
      case FileListItemLayout.GridSimple:
        const columns = Math.floor(widthFull / cellSize);
        rect.width = widthFull / columns;
        rect.height = rect.width; // folders > 0 && index <= folders ? 60 : cellSize;
        return;
    }
  }), [view?.folders, viewtype, width]);

  const popup = usePopup();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const noop = useCallback(() => {}, []);

  // Scroll list to top when folder changes
  useLayoutEffect(() => {
    setTimeout(() => {
      filelist?.current?.scrollToTop(false);
    }, 0);
  }, [folderKey]);

  // List hotkeys
  useLayoutEffect(() => {
    const keyDown = events.addKeyListener('down', (e) => {
      switch (e.code) {
        // Navigate up list
        case 'ArrowUp':
          if (!popup.visible) {
            setFocusedI(focusedI - 1);
            // filelist.current.scrollToIndex(focusedI - 1);
          }
          break;
        // Navigate down list
        case 'ArrowDown':
          if (!popup.visible) {
            setFocusedI(focusedI + 1);
            // filelist.current.scrollToIndex(focusedI + 1);
          }
          break;
        // Navigate to top of list
        case 'ArrowLeft':
          if (!popup.visible) {
            filelist?.current?.scrollToTop();
          }
          break;
        // Navigate to bottom of list
        case 'ArrowRight':
          if (!popup.visible) {
            filelist?.current?.scrollToEnd();
          }
          break;
        // Select all
        case 'KeyA':
          if (e.flags.ctrlKey || e.flags.metaKey) {
            const ids = itemList.filter(e => e !== 'folders' && e !== 'files');
            dispatch(slices.files.actions.select({ids, noToggle: true}));
          }
          break;
        // Clear selection
        case 'Escape':
          dispatch(slices.files.actions.select({ids: []}));
          break;
      }
      return true;
    });

    return () => {
      keyDown.unsubscribe();
    };

  }, [itemList, popup, focusedI]);

  const open = useCallback(async (item: FilesItem) => {
    if (props.onOpen) {
      props.onOpen(item);
      return;
    }

    const {id, url, type, name} = item;
    if (type === 'folder') {
      navigate(`/folder/${id}`);
    } else {
      if (isWeb()) {
        download(url);
      } else {
        dispatch(slices.files.actions.preview({id, name}));
        await preview(id, name, dispatch, slices.files.actions);
        dispatch(slices.files.actions.previewClose());
      }
    }
  }, []);
  
  const select = useCallback((item: FilesItem, e?: GestureResponderEvent) => {
    const [isShift, _isCtrl] = [e?.shiftKey, e?.metaKey || e?.ctrlKey];
    if (isShift && focused) {
      setRngSelect(true);
      dispatch(slices.files.actions.select({ids: [item.id], focused, view}));
    } else {
      focusedRef.current = item ? item.id : '';
      setRngSelect(false);
      dispatch(slices.files.actions.select({ids: [item.id]}));
    }
  }, [focused, view]);

  const focus = useCallback((item?: FilesItem) => {
    if (rngSelect) return;
    focusedRef.current = item ? item.id : '';
  }, [focused]);

  const resize = useCallback((e: LayoutChangeEvent) => {
    setWidth(e.nativeEvent.layout.width);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await load(navigate, dispatch, folderKey);
    setLoading(false);
  }, []);

  const renderFooter = useCallback(() => <View style={styles.footer}/>, []);

  const renderItem = useCallback((type: number, id: string, _index: number, ext: typeof extended) => {
    if (id === 'files' || id === 'folders') {
      return (
        <FileListHeader name={id} showColumns={hasColumns}/>
      );
    }

    return (
      <FileListItem
        item={ext.items[id]}
        layout={type}
        focused={hasFocus && ext.focused === id}
        isPremium={isPremium}
        rootFolder={folderKey}
        disableDrag={!hasDrag}
        disableOpen={!hasOpen}
        disableFocus={!hasFocus}
        disableSelect={!hasSelect}
        dragItems={hasDrag ? ext.dragging : []}
        selectItems={hasSelect ? ext.selection : []}
        hideIcons={props.options?.hideIcons}
        hideExtraIcons={props.options?.hideExtraIcons}
        hasPrivate={hasPrivate}
        linkParent={props.options?.linkParent}
        onSelect={hasSelect ? select : open}
        onFocus={hasFocus ? focus : noop}
        onOpen={hasOpen ? open : noop}
      />
    );
  }, [
    width,
    hasGridView,
    hasColumns,
    focused,
    isPremium,
    hasDrag,
    hasSelect,
    hasOpen,
    hasFocus,
  ]);

  return listData.getSize() > 0 ? (
    <View
      style={[styles.root, {paddingLeft: listGutter}]}
      removeClippedSubviews={false}
      onLayout={resize}>
      <RecyclerListView
        ref={filelist}
        key={JSON.stringify(screen)}
        rowRenderer={renderItem}
        renderFooter={renderFooter}
        layoutProvider={listLayout}
        dataProvider={listData}
        extendedState={extended}
        renderAheadOffset={screen.height / 2}
        scrollViewProps={{
          refreshControl: hasPullToRefresh ? (
            <RefreshControl
              colors={[colors.primary.brand]}
              refreshing={isLoading}
              onRefresh={refresh}
            />
          ) : undefined,
        }}
      />
    </View>
  ) : null;
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  footer: {
    height: 100,
  },
});
